import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CryptoJS from 'crypto-js';
import { getMetadata } from '../utils/encryptedStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

/**
 * GmailTokenWarning
 * -----------------
 * Global component that checks Gmail token status on app load.
 * Shows a non-blocking banner in the footer area when:
 * - Refresh token is missing/invalid (connection lost)
 * - Token was revoked by the user in Google settings
 * 
 * NOTE: Access token expiry (~1 hour) is normal and tokens are
 * auto-refreshed. We do NOT warn about access token expiry.
 * 
 * The banner is shown at the bottom of the app and can be dismissed.
 * If the user clicks "Reconnect Gmail", it opens the OAuth flow.
 */
const GmailTokenWarning = () => {
  const [status, setStatus] = useState(null); // { expired, connected, gmail_email }
  const [dismissed, setDismissed] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const checkGmailStatus = useCallback(async () => {
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) return;

      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/gmail/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && typeof data.data === 'string') {
          const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
          const decrypted = bytes.toString(CryptoJS.enc.Utf8);
          const statusData = JSON.parse(decrypted);
          setStatus(statusData);

          // Only show if the connection is expired (refresh token missing/revoked)
          if (!statusData.expired) {
            setStatus(null);
          }
        }
      }
    } catch (err) {
      // Silent fail - this is a non-critical check
      console.error('Gmail status check failed:', err);
    }
  }, []);

  useEffect(() => {
    // Check on mount (app load / login)
    checkGmailStatus();

    // Re-check every 6 hours
    const interval = setInterval(checkGmailStatus, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkGmailStatus]);

  // Listen for OAuth success to clear warning
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'GMAIL_CONNECTED' && event.data.success) {
        setStatus(null);
        setDismissed(false);
        setReconnecting(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) return;

      const callbackUrl = `${window.location.origin}/gmail-callback`;
      const payload = { cid, callback_url: callbackUrl };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/gmail/auth-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();
      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const result = JSON.parse(decrypted);

        if (result.auth_url) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          window.open(
            result.auth_url,
            'Gmail OAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );
        }
      }
    } catch (err) {
      console.error('Reconnect Gmail error:', err);
    } finally {
      setReconnecting(false);
    }
  };

  // Don't show on public/external-facing pages (candidates should never see internal warnings)
  const publicPrefixes = ['/auth/', '/interviews/book/', '/interview/take/', '/ai-interview/launch/', '/reschedule-form/', '/calendar-callback', '/gmail-callback', '/outlook-callback', '/change-mail'];
  const currentPath = window.location.pathname;
  if (publicPrefixes.some(p => currentPath.startsWith(p))) {
    return null;
  }

  // Don't show if no expired state, or if dismissed
  if (!status || dismissed || !status.expired) {
    return null;
  }

  const isExpired = status.expired;
  const bannerContent = (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between shadow-[0_-2px_10px_rgba(0,0,0,0.15)] ${
        isExpired
          ? 'bg-red-600 text-white'
          : 'bg-orange-500 text-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span className="text-sm font-medium">
          {`Gmail connection lost${status.gmail_email ? ` for ${status.gmail_email}` : ''}. Please reconnect to continue sending emails.`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleReconnect}
          disabled={reconnecting}
          className="px-3 py-1 text-xs font-semibold rounded-md transition-colors bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          {reconnecting ? 'Connecting...' : 'Reconnect Gmail'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-white/20 rounded"
          title="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  return createPortal(bannerContent, document.body);
};

export default GmailTokenWarning;
